import { CoreService } from "@/services/core/CoreService";
import { createExpiredLoginPath, getCurrentAppPath } from "@/utils/authRedirect";

export class BaseApiService {
  protected coreService: CoreService;

  constructor() {
    this.coreService = CoreService.getInstance();
  }

  private async fetchResponseWithAuth(
    endpoint: string,
    options: RequestInit = {},
    baseUrl: string,
    accept = "application/json",
  ): Promise<Response> {
    const authHeader = this.coreService.getAuthorizationHeader();

    if (!authHeader) {
      throw new Error("You must be logged in to access this resource");
    }

    //auth header is always stored with out the prefix.
    const token = authHeader.startsWith("Bearer ") ? authHeader : `Bearer ${authHeader}`;

    const fullUrl = `${baseUrl}${endpoint}`;

    // Default headers for JSON API
    const headers: Record<string, string> = {
      Accept: accept,
      Authorization: token,
    };

    headers["Content-Type"] = "application/json";

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...headers,
          ...(options.headers as Record<string, string>),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          // Special handling for user profile API calls
          if (endpoint.includes("/user/") && !window.location.pathname.startsWith("/login")) {
            // Check if the endpoint is for the current user's profile
            const storedUser = this.coreService.getStoredUser();
            const currentUserId = storedUser?.id;

            // Only logout and redirect if not related to profile or it's a critical auth error
            if (!currentUserId || !endpoint.includes(`/user/${currentUserId}`)) {
              this.coreService.logout();
              window.location.href = createExpiredLoginPath(getCurrentAppPath(window.location));
            }

            throw new Error("Unable to access requested resource");
          } else {
            // Standard handling for other 401 errors
            this.coreService.logout();
            window.location.href = createExpiredLoginPath(getCurrentAppPath(window.location));
            throw new Error("Your session has expired. Please log in again.");
          }
        }

        if (response.status === 403) {
          throw new Error("You do not have permission to perform this action.");
        }

        let errorMessage = `API request failed: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          const metadataErrors = Array.isArray(errorData.metadata?.errors)
            ? errorData.metadata.errors.filter(
                (entry: unknown): entry is string => typeof entry === "string" && entry.length > 0,
              )
            : [];
          errorMessage =
            metadataErrors.join(", ") ||
            errorData.metadata?.message ||
            errorData.message ||
            errorData.error ||
            errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return response;
    } catch (error) {
      // Handle CORS errors specifically
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        throw new Error(
          "Unable to connect to the server. This may be due to a CORS issue or network problem. Please check your connection or contact support.",
        );
      }
      throw error;
    }
  }

  protected async fetchWithAuth<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
    baseUrl: string,
  ): Promise<T> {
    const response = await this.fetchResponseWithAuth(endpoint, options, baseUrl);

    // Successful mutation endpoints may return 204 with no response body.
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  protected async get<T>(endpoint: string, baseUrl: string): Promise<T> {
    return this.fetchWithAuth(endpoint, { method: "GET" }, baseUrl);
  }

  protected async getBlob(endpoint: string, baseUrl: string): Promise<Blob> {
    const response = await this.fetchResponseWithAuth(
      endpoint,
      { method: "GET" },
      baseUrl,
      "application/x-ndjson",
    );
    return response.blob();
  }

  protected async post<T>(endpoint: string, data: any, baseUrl: string): Promise<T> {
    return this.fetchWithAuth(
      endpoint,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      baseUrl,
    );
  }

  protected async put<T>(endpoint: string, data: any, baseUrl: string): Promise<T> {
    return this.fetchWithAuth(
      endpoint,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      baseUrl,
    );
  }

  protected async patch<T>(endpoint: string, data: any, baseUrl: string): Promise<T> {
    return this.fetchWithAuth(
      endpoint,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
      baseUrl,
    );
  }

  protected async delete<T>(endpoint: string, baseUrl: string): Promise<T> {
    const response = await this.fetchResponseWithAuth(endpoint, { method: "DELETE" }, baseUrl);

    // REST deletion endpoints commonly return 204 with no response body.
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }
}
